import React, { useContext, useState } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import Modal from './Modal';
import PlayersList from './PlayersList';
import GameCardContent from './GameCardContent';
import PeopleIcon from '@mui/icons-material/People';

/**
 * GameCard component displays information about a game in a tournament
 * 
 * @param {Object} props - Component props
 * @param {string} props.image - URL of the game image
 * @param {string} props.title - Title of the game
 * @param {number} props.rank - Player's rank in the game
 * @param {number} props.score - Player's score in the game
 * @param {string} props.initials - Player's initials in the game
 * @param {number} props.points - Points earned by the player in the game
 * @param {boolean} props.isRanked - Whether the player is ranked in this game
 * @param {string} props.username - Username of the player
 * @param {Array} props.leaderboard - Array of player objects for the game
 * @param {boolean} props.isHighestRank - Whether this game has the player's highest rank
 * @param {boolean} props.isLowestRank - Whether this game has the player's lowest rank
 */
function GameCard({ 
  image, 
  title, 
  rank, 
  score, 
  initials, 
  points, 
  isRanked = true, 
  username, 
  leaderboard = [],
  isHighestRank = false,
  isLowestRank = false
}) {
  const { theme } = useContext(ThemeContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  
  // Create a user entry object from props
  const userEntry = isRanked ? {
    rank,
    score,
    initials,
    points
  } : null;
  
  // Determine CSS classes for highlighting
  const cardClasses = [
    'game-card',
    theme,
    isHighestRank ? 'best-rank' : '',
    isLowestRank && isRanked ? 'worst-rank' : '',
    !isRanked ? 'unranked' : ''
  ].filter(Boolean).join(' ');
  
  return (
    <div className={cardClasses}>
      {/* Game image with title overlay */}
      <div className="game-image-container">
        {image && (
          <img 
            src={image} 
            alt={title} 
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        
        {/* Game title */}
        <div className="game-title">{title}</div>
        
        {/* Rank display */}
        {isRanked && rank && (
          <div className="game-rank">#{rank}</div>
        )}

        {/* Score and initials overlay */}
        {isRanked && (
          <>
            {initials && <div className="game-initials">{initials}</div>}
            {score && <div className="game-score">{score.toLocaleString()}</div>}
          </>
        )}
      </div>
      
      {/* User information */}
      <div className="user-info">
        <GameCardContent 
          username={username}
          userEntry={userEntry}
          isRanked={isRanked}
          leaderboard={leaderboard}
        />
        
        {/* View all players button (square icon) */}
        <button 
          className="view-players-icon-button"
          onClick={openModal}
          aria-label="View All Players"
          title="View All Players"
        >
          <PeopleIcon />
        </button>
      </div>
      
      {/* Players Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        title="Game Leaderboard"
        size="large"
      >
        {leaderboard && leaderboard.length > 0 ? (
          <PlayersList 
            players={leaderboard} 
            currentUsername={username}
            gameTitle={title}
            image={image}
          />
        ) : (
          <div className="no-leaderboard-data">
            No player data available for this game.
          </div>
        )}
      </Modal>
    </div>
  );
}

export default GameCard;